import React, { useState } from "react";
import { Text } from "@geist-ui/core";
import { Badge, Flex } from "@radix-ui/themes";

import { ConversationHistory } from "@/components/shared/conversationHistory";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { ReleaseConversation } from "@/v2/entities/release-conversation/ReleaseConversation.model";

import styles from "./styles.module.scss";

interface InstructionsFeedbackProps {
  topic: string;
  topics: Topic[] | null;
  topicConversations: ReleaseConversation[];
  goodCount: number;
  badCount: number;
  noFeedbackCount: number;
}

export const InstructionsFeedback: React.FC<InstructionsFeedbackProps> = ({
  topicConversations,
  goodCount,
  badCount,
  noFeedbackCount,
}) => {
  const [selectedConversation, setSelectedConversation] =
    useState<ReleaseConversation | null>(null);

  return (
    <div className={styles.feedbackSection}>
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between">
          <Flex align="center" gap="2">
            <Text style={{ fontSize: "14px", fontWeight: 500 }}>Sessions</Text>
          </Flex>
        </Flex>

        {topicConversations.length === 0 ? (
          <div
            className={styles.emptyStateContainer}
            style={{
              padding: "16px",
              textAlign: "center",
              width: "100%",
            }}
          >
            <Text small style={{ color: "var(--gray-9)" }}>
              No sessions available. They will appear here once users start
              interacting with this topic.
            </Text>
          </div>
        ) : (
          <>
            <Flex gap="3" className={styles.feedbackRow}>
              {/* Good Feedback */}
              <Flex
                align="center"
                gap="1"
                style={{ cursor: goodCount > 0 ? "pointer" : "default" }}
                onClick={() => {
                  if (goodCount > 0) {
                    const goodFeedbackConvs = topicConversations.filter(
                      (conv) => conv.feedback === "POSITIVE"
                    );
                    setSelectedConversation(goodFeedbackConvs[0]);
                  }
                }}
              >
                <Badge
                  size="2"
                  className={styles.feedbackBadge}
                  data-color="green"
                >
                  {goodCount}
                </Badge>
                <Text small style={{ color: "var(--gray-11)" }}>
                  Good
                </Text>
              </Flex>

              {/* Bad Feedback */}
              <Flex
                align="center"
                gap="1"
                style={{ cursor: badCount > 0 ? "pointer" : "default" }}
                onClick={() => {
                  if (badCount > 0) {
                    const badFeedbackConvs = topicConversations.filter(
                      (conv) => conv.feedback === "NEGATIVE"
                    );
                    setSelectedConversation(badFeedbackConvs[0]);
                  }
                }}
              >
                <Badge
                  size="2"
                  className={styles.feedbackBadge}
                  data-color="red"
                >
                  {badCount}
                </Badge>
                <Text small style={{ color: "var(--gray-11)" }}>
                  Bad
                </Text>
              </Flex>

              {/* No Feedback */}
              <Flex
                align="center"
                gap="1"
                style={{
                  cursor: noFeedbackCount > 0 ? "pointer" : "default",
                }}
                onClick={() => {
                  if (noFeedbackCount > 0) {
                    const noFeedbackConvs = topicConversations.filter(
                      (conv) =>
                        conv.feedback !== "POSITIVE" &&
                        conv.feedback !== "NEGATIVE"
                    );
                    setSelectedConversation(noFeedbackConvs[0]);
                  }
                }}
              >
                <Badge
                  size="2"
                  className={styles.feedbackBadge}
                  data-color="gray"
                >
                  {noFeedbackCount}
                </Badge>
                <Text small style={{ color: "var(--gray-11)" }}>
                  No Feedback
                </Text>
              </Flex>
            </Flex>
          </>
        )}
      </Flex>

      {selectedConversation && (
        <ConversationHistory
          open={!!selectedConversation}
          onOpenChange={(open) => !open && setSelectedConversation(null)}
          conversation={selectedConversation}
        />
      )}
    </div>
  );
};
